import {
  chaveDeDataDeCalendario,
  chaveDoDiaEmSaoPaulo,
  formatDateLongWithWeekday,
} from '@/shared/formatters';
import { RITO_LABELS } from '@/shared/constants/rito';

import type {
  ArbitragemAndamento,
  ArbitragemAndamentoDia,
  ArbitragemAndamentoIcone,
  ArbitragemCampoExplicado,
  ArbitragemDetalhe,
  ArbitragemEtapa,
  ArbitragemParticipante,
  ArbitragemParticipantePapel,
  ArbitragemPendencia,
  ArbitragemPendenciaResolvida,
  ArbitragemSituacao,
} from '../types/arbitragemDetalheTypes';
import type { CitacaoArbitralDetail, CitacaoArbitralParte } from '../types/portalArbitralTypes';

import { montarComplementosMock } from './arbitragemComplementosMock';
import { ehArbitragemMock } from './arbitragensMock';
import { diasAtePrazo } from './chipProcesso';
import { isSituacaoRespondivel } from './orientacaoDoDetalhe';
import { statusCasoRotulo } from './statusRotulos';

const PAPEL_ROTULO_LITISCONSORCIO: Record<'REQUERENTE' | 'REQUERIDO', string> = {
  REQUERENTE: 'Quem abriu o caso',
  REQUERIDO: 'Também foi notificado no caso',
};

const construirCamposReais = (citacao: CitacaoArbitralDetail): ArbitragemCampoExplicado[] => {
  const campos: ArbitragemCampoExplicado[] = [
    {
      id: 'inicio',
      rotulo: 'Quando o caso começou',
      valor: formatDateLongWithWeekday(citacao.dataCitacao),
      ajuda: 'Data em que o pedido foi registrado.',
    },
    {
      id: 'forma',
      rotulo: 'Como o caso será resolvido',
      valor: 'Arbitragem',
      ajuda:
        'Um especialista neutro decide quem tem razão, sem precisar ir ao fórum. A decisão vale como uma sentença.',
    },
    {
      id: 'competencia',
      rotulo: 'Por que este caso veio para cá',
      valor: 'Estava escrito no contrato',
      ajuda: 'O contrato que você assinou diz que problemas seriam resolvidos pela Arbitralis.',
    },
  ];
  const rito = citacao.rito ? RITO_LABELS[citacao.rito] : undefined;
  if (rito) {
    campos.push({ id: 'rito', rotulo: 'Rito', valor: rito, ajuda: '' });
  }
  return campos;
};

const derivarParticipantesDePartes = (
  partes: CitacaoArbitralParte[],
  nomeFantasiaRemetente: string,
): ArbitragemParticipante[] =>
  partes.map((parte) => {
    if (parte.isMe) {
      return { id: parte.id, nome: parte.nome ?? 'Você', papel: 'VOCE', papelRotulo: 'Você' };
    }
    const papel: ArbitragemParticipantePapel = parte.papel;
    const nomeFallback = papel === 'REQUERENTE' ? nomeFantasiaRemetente : 'Requerido';
    return {
      id: parte.id,
      nome: parte.nome ?? nomeFallback,
      papel,
      papelRotulo: PAPEL_ROTULO_LITISCONSORCIO[parte.papel],
    };
  });

const ICONES_ANDAMENTO_VALIDOS: ReadonlySet<string> = new Set([
  'abertura',
  'confirmacao',
  'documento',
]);

const normalizarIconeAndamento = (icone: ArbitragemAndamentoIcone): ArbitragemAndamentoIcone =>
  ICONES_ANDAMENTO_VALIDOS.has(icone) ? icone : 'abertura';

// As sete macroetapas fixas (Jubileu — iniciativa "Painel do notificado: Arbitragem", ClickUp
// 86ak8mece). Textos EXATOS da especificação (coluna "Texto simples esperado"). NUNCA derivar a
// etapa atual daqui no front — `citacao.etapaAtual` já vem pronto do backend
// (`citacaoPortalService.derivarEtapaAtual`); esta lista só empresta título/descrição fixos.
const ETAPAS_FIXAS: ReadonlyArray<{
  numero: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  titulo: string;
  descricao: string;
}> = [
  {
    numero: 1,
    titulo: 'Você recebeu o caso',
    descricao: 'A citação chegou e liberou seu acesso a esta página.',
  },
  {
    numero: 2,
    titulo: 'Sua vez de responder',
    descricao: 'Você pode contar sua versão e enviar documentos dentro do prazo.',
  },
  {
    numero: 3,
    titulo: 'Análise das respostas',
    descricao: 'A Arbitralis organiza o que foi enviado antes de levar o caso ao especialista.',
  },
  {
    numero: 4,
    titulo: 'Escolha do especialista',
    descricao: 'Um profissional neutro é escolhido para decidir o caso.',
  },
  {
    numero: 5,
    titulo: 'Análise pelo especialista',
    descricao: 'O especialista analisa o caso e pode determinar outras providências.',
  },
  {
    numero: 6,
    titulo: 'Decisão final',
    descricao: 'A decisão foi publicada e abriu prazo curto para pedir esclarecimentos.',
  },
  {
    numero: 7,
    titulo: 'Caso encerrado',
    descricao: 'A decisão tornou-se definitiva e o processo foi concluído.',
  },
];

// Mesmo padrão do painel do árbitro (A9): a etapa ATUAL some para o TOPO da lista renderizada,
// destacada; a nº 1 fica embaixo. Só a ORDEM DE EXIBIÇÃO inverte — a numeração é sempre 1-7.
export const derivarEtapasFixas = (etapaAtual: number): ArbitragemEtapa[] =>
  [...ETAPAS_FIXAS]
    .map((etapa) => ({
      id: `etapa-${etapa.numero}`,
      numero: etapa.numero,
      titulo: etapa.titulo,
      descricao: etapa.descricao,
      estado: (etapa.numero < etapaAtual
        ? 'concluida'
        : etapa.numero === etapaAtual
          ? 'atual'
          : 'futura') as ArbitragemEtapa['estado'],
      // Sem timestamp por macroetapa (dado que não existe no domínio) — `EtapaDescricao` já
      // degrada para "Concluído" plano quando `concluidaEmIso` é `null`.
      concluidaEmIso: null,
      observacaoAtual: null,
    }))
    .reverse();

const derivarParticipantes = (
  citacao: CitacaoArbitralDetail,
  requerenteNome: string,
): ArbitragemParticipante[] => {
  const partesDoCaso: ArbitragemParticipante[] =
    citacao.partes && citacao.partes.length > 0
      ? derivarParticipantesDePartes(citacao.partes, citacao.remetente.nomeFantasia)
      : [
          {
            id: 'requerente',
            nome: requerenteNome,
            papel: 'REQUERENTE',
            papelRotulo: 'Quem abriu o caso',
          },
          {
            id: 'voce',
            nome: citacao.requeridoNome ?? 'Você',
            papel: 'VOCE',
            papelRotulo: 'Você',
          },
        ];
  const participantes: ArbitragemParticipante[] = [
    ...partesDoCaso,
    {
      id: 'secretaria',
      nome: 'Secretaria da Arbitralis',
      papel: 'SECRETARIA',
      papelRotulo: 'Organiza o caso e acompanha os prazos',
    },
  ];
  if (citacao.arbitroNome) {
    participantes.push({
      id: 'especialista',
      nome: citacao.arbitroNome,
      papel: 'ESPECIALISTA',
      papelRotulo: 'Árbitro neutro que vai decidir',
    });
  }
  return participantes;
};

const derivarSituacao = (citacao: CitacaoArbitralDetail): ArbitragemSituacao => {
  if (citacao.processoEncerrado || citacao.status === 'CONCLUIDO') return 'encerrado';
  if (citacao.status === 'BLOQUEADA_CONTATO' || citacao.status === 'NAO_LOCALIZADO_PORTAL') {
    return 'contato_pendente';
  }
  // Sentença publicada mas o processo ainda não é terminal (aguardando trânsito em julgado —
  // ver `sentencaEmitida` no backend). Vem ANTES de `manifestou`: é a novidade mais recente do
  // caso, independente de já ter respondido antes.
  if (citacao.sentencaEmitida) return 'sentenca_publicada';
  if (citacao.manifestacaoExistente) return 'manifestou';
  // `diasAtePrazo` (compartilhado com o chip da lista) e a fronteira `<= 0` valem para a
  // feature inteira: vencido no limite já é perdido — com `< 0`, um prazo vencido há menos
  // de 24h dava CTA que redirecionava de volta (botão morto).
  const dias = citacao.prazoDataLimite === null ? null : diasAtePrazo(citacao.prazoDataLimite);
  if (dias !== null && dias <= 0) return 'prazo_perdido';
  // Depois da citação a resposta já está liberada: AGUARDANDO_LEITURA e
  // PRIMEIRA_TENTATIVA já significam "é a sua vez". Nada fica "em análise pela
  // Arbitralis" à vista do notificado nesta fase.
  //
  // `canRespond` do backend veta AQUI, na fonte: prazo aparentemente aberto no cliente com
  // resposta barrada pela API (403) é divergência cliente↔backend — tag, tom, banner e CTA
  // derivam todos desta mesma `situacao`, então o remap não pode viver só na apresentação.
  if (!citacao.canRespond) return 'dessincronizado';
  return dias !== null && dias <= 3 ? 'prazo_acabando' : 'prazo_aberto';
};

// A tag acompanha a situação derivada, não o status cru: com `status=ENVIANDO` e prazo aberto
// o rótulo cru diria "Em análise pela Arbitralis" enquanto "Situação agora" diz "Sua resposta
// está liberada" — instrução da Bianca é a tag concordar com a resposta. No veto
// (`dessincronizado`) e no prazo perdido o rótulo cru de AGUARDANDO_LEITURA diria "Sua vez de
// responder" com a resposta barrada; "Prazo encerrado" reusa o rótulo do chip da lista.
const derivarStatusRotulo = (
  citacao: CitacaoArbitralDetail,
  situacao: ArbitragemSituacao,
  podeManifestar: boolean,
): string => {
  if (citacao.processoEncerrado) return 'Caso encerrado';
  if (podeManifestar) return 'Sua vez de responder';
  if (situacao === 'prazo_perdido') return 'Prazo encerrado';
  if (situacao === 'dessincronizado') return 'Status indisponível';
  return statusCasoRotulo(citacao.status);
};

export const montarArbitragemDetalhe = (citacao: CitacaoArbitralDetail): ArbitragemDetalhe => {
  const requerenteNome = citacao.requerenteNome ?? citacao.remetente.nomeFantasia;
  const situacao = derivarSituacao(citacao);
  // O veto do `canRespond` já entrou em `derivarSituacao` (vira `dessincronizado`), então
  // situação respondível implica resposta aceita pela API.
  const podeManifestar = isSituacaoRespondivel(situacao);
  const complementos = montarComplementosMock({
    requerenteNome,
    dataCitacaoIso: citacao.dataCitacao,
    agoraIso: new Date().toISOString(),
  });
  const andamentos: ArbitragemAndamento[] =
    citacao.andamentos?.map((andamento) => ({
      ...andamento,
      icone: normalizarIconeAndamento(andamento.icone),
    })) ?? complementos.andamentos;
  // Fora do modo demo, uma citação real sem o campo não pode cair no mock: os IDs do mock
  // (pendencia-comprovante, pendencia-doc-determinacao) já estão ligados a endpoints reais
  // de download/resolução e um backend real não os reconhece.
  const ehMock = ehArbitragemMock(citacao.id);
  const pendencias: ArbitragemPendencia[] = citacao.pendencias
    ? citacao.pendencias.map((pendencia) => ({
        ...pendencia,
        abreImpugnacaoDoEspecialista: pendencia.abreImpugnacaoDoEspecialista === true,
        prazoLimiteIso:
          pendencia.prazoLimiteIso ??
          chaveDeDataDeCalendario(citacao.prazoDataLimiteDia) ??
          chaveDeDataDeCalendario(citacao.prazoDataLimite) ??
          new Date().toISOString(),
      }))
    : ehMock
      ? complementos.pendencias
      : [];
  const pendenciasResolvidas: ArbitragemPendenciaResolvida[] =
    citacao.pendenciasResolvidas ?? (ehMock ? complementos.pendenciasResolvidas : []);
  // Regra completa de "precisa agir" (Jubileu, ClickUp 86ak8mece): vez de responder, pendência
  // aberta, OU janela de Pedido de Esclarecimento aberta pós-sentença — as três, nunca só a
  // primeira. Sem a 3ª condição, "Você não precisa agir" ficava preso mesmo com o prazo de
  // esclarecimento correndo (o bug que esta tarefa corrige).
  const podePedirEsclarecimento = !!citacao.esclarecimento?.podePedir;
  const precisaAgir =
    podeManifestar ||
    situacao === 'prazo_perdido' ||
    situacao === 'contato_pendente' ||
    podePedirEsclarecimento ||
    Math.max(citacao.pendenciasAbertas ?? 0, pendencias.length) > 0;

  return {
    id: citacao.id,
    numeroCaso: citacao.numeroProcesso,
    tituloCaso: 'Processo de arbitragem',
    introducao:
      'Alguém abriu um caso contra você na Arbitralis. Aqui você vê o que está sendo pedido, os documentos e tudo o que já aconteceu.',
    statusRotulo: derivarStatusRotulo(citacao, situacao, podeManifestar),
    requerenteNome,
    situacaoAgoraRotulo: podeManifestar
      ? 'Sua resposta está liberada'
      : precisaAgir
        ? 'Você precisa agir'
        : 'Você não precisa agir',
    precisaAgir,
    podePedirEsclarecimento,
    esclarecimentoJaPedido: !!citacao.esclarecimento?.jaPediu,
    prazoEsclarecimentoDiasRestantes: citacao.esclarecimento?.diasRestantes ?? null,
    etapas: derivarEtapasFixas(citacao.etapaAtual),
    pendencias,
    pendenciasResolvidas,
    participantes: derivarParticipantes(citacao, requerenteNome),
    podeManifestar,
    valorCobrado: citacao.valorCobrado ?? complementos.valorCobrado,
    andamentos,
    documentos: citacao.documentos ?? complementos.documentos,
    campos: ehMock ? complementos.campos : construirCamposReais(citacao),
    relatoOutraParte: citacao.relatoOutraParte ?? (ehMock ? complementos.relatoOutraParte : null),
    advogadosConvidados: citacao.advogadosConvidados ?? [],
    // Ausente (core sem o campo, janela de deploy) fecha: o core recusaria o pedido de qualquer jeito.
    impugnacaoNoPrazo: citacao.impugnacaoNoPrazo === true,
    impugnacaoEmAnalise: citacao.impugnacaoEmAnalise === true,
    prazoMedioDias: complementos.prazoMedioDias,
    situacao,
    sentencaPublicadaEmIso: citacao.sentencaPublicadaEm ?? null,
    // `prazoLimiteIso` é reduzido a DIA por `derivarPrazoPendencia` (via `chaveDoDiaEmUtc`, que
    // pressupõe valor-DATA à meia-noite UTC — a convenção das pendências). O prazo de contestação
    // NÃO é desse tipo: é o instante de preclusão, e lê-lo em UTC dava o dia seguinte. Entregar
    // aqui o dia já resolvido em SP (`prazoDataLimiteDia`) faz o campo voltar a ter uma natureza
    // só, e todo o resto do portal segue igual (ARB-202608-000117).
    // `chaveDeDataDeCalendario` normaliza as duas formas (chave nua e instante, este reduzido ao
    // dia de SP) e recusa lixo. Sem ela o front confia que o backend serializa o campo como
    // `YYYY-MM-DD`: se algum dia mandar instante — o mesmo deslize que originou o incidente —,
    // `derivarPrazoPendencia` volta a lê-lo em UTC e o off-by-one reaparece em silêncio.
    prazoLimiteIso:
      chaveDeDataDeCalendario(citacao.prazoDataLimiteDia) ??
      chaveDeDataDeCalendario(citacao.prazoDataLimite) ??
      citacao.prazoDataLimite,
  };
};

const chaveDoDia = (iso: string): string => chaveDoDiaEmSaoPaulo(iso) ?? iso.slice(0, 10);

export const agruparAndamentosPorDia = (
  andamentos: ArbitragemAndamento[],
): ArbitragemAndamentoDia[] => {
  const ordenados = [...andamentos].sort(
    (a, b) => Date.parse(b.ocorridoEmIso) - Date.parse(a.ocorridoEmIso),
  );

  return ordenados.reduce<ArbitragemAndamentoDia[]>((dias, andamento) => {
    const dia = chaveDoDia(andamento.ocorridoEmIso);
    const ultimo = dias.at(-1);

    if (ultimo && ultimo.diaIso === dia) {
      ultimo.itens.push(andamento);
      return dias;
    }

    dias.push({ diaIso: dia, itens: [andamento] });
    return dias;
  }, []);
};
